import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import styled from "styled-components";

import { useEmbedParametersContext } from "../../../contexts/embedParametersContext";
import {
  getTimeRemaining,
  secondsToHHMMSSFormat
} from "../../../helpers/timeHelpers";
import i18n from "../../../service/i18n";
import { usePlayerStore } from "../../../store/usePlayerStore";
import { progressBarDisabledColor } from "../../../styles/variables";
import {
  BufferProgress,
  ElapsedProgress,
  Progress,
  SeekSlider,
  Wrapper
} from "../../share/ProgressBar";
import { ThemeColors } from "../getThemeColors";

const Time = styled.time<{
  iconFillColor: string;
}>`
  color: ${props => props.iconFillColor};
  font-size: 0.875rem;
  line-height: 150%;
  margin: 0 4px;
`;

const parseEventValue = event =>
  parseInt((event.target as HTMLInputElement).value, 10);

const getFormattedTimeRemaining = (
  currentTime: number,
  durationInSeconds?: number
): string => {
  if (!durationInSeconds) {
    return "";
  }

  return `-${secondsToHHMMSSFormat(
    getTimeRemaining(currentTime, durationInSeconds)
  )}`;
};

const getSliderValueFromTouch = (
  touches: React.TouchList,
  slider: HTMLInputElement
): number => {
  const touchX = touches[0].clientX;
  const sliderRect = slider.getBoundingClientRect();

  const relativeX = touchX - sliderRect.left;
  const sliderWidth = sliderRect.width;
  const sliderMaxValue = parseFloat(slider.max);
  const newValue = Math.round((relativeX / sliderWidth) * sliderMaxValue);
  return Math.min(Math.max(newValue, 0), sliderMaxValue);
};

const AudioTimeLine = ({ themeColors }: { themeColors: ThemeColors }) => {
  const { getState, subscribe, useStoreState } = usePlayerStore();
  const buffered = useStoreState.buffered();
  const device = useStoreState.device();
  const durationInSeconds = useStoreState.durationInSeconds();
  const isLive = useStoreState.isLive();
  const player = useStoreState.player();

  const { language } = useEmbedParametersContext();

  const [isDraggingTimeline, setIsDraggingTimeline] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(
    getState().isLive ? 1 : getState().currentTime
  );

  const audioHasVolumeWhenSeekingStarts = useRef(!getState().isMuted);
  const sliderRef = useRef<HTMLInputElement>(null);

  const disabled =
    isLive || (durationInSeconds !== undefined && durationInSeconds < 0);
  const duration = isLive ? 1 : durationInSeconds;

  useEffect(() => {
    // In live, the elapsed time is always 1 because timeline is not seekable
    if (isLive) {
      setElapsedTime(1);
    }
  }, [isLive]);

  useEffect(() => {
    if (isDraggingTimeline || isLive) {
      return;
    }
    const unsubscribe = subscribe(
      state => state.currentTime,
      currentTime => {
        setElapsedTime(currentTime);
      },
      {
        fireImmediately: true
      }
    );

    return () => {
      unsubscribe();
    };
  }, [isDraggingTimeline, isLive, subscribe]);

  const onSeekStart = useCallback(
    (time: number) => {
      const hasVolume = !getState().isMuted;
      audioHasVolumeWhenSeekingStarts.current = hasVolume;
      setIsDraggingTimeline(true);
      setElapsedTime(time);
      if (hasVolume) {
        player.setMuted(true);
      }
    },
    [getState, player]
  );

  const onSeeking = useCallback(
    (time: number) => {
      if (isDraggingTimeline) {
        setElapsedTime(time);
      } else {
        player.seekTo(time);
      }
    },
    [isDraggingTimeline, player]
  );

  const onSeekEnd = useCallback(() => {
    if (audioHasVolumeWhenSeekingStarts.current) {
      player.setMuted(false);
    }
    setIsDraggingTimeline(false);
    player.seekTo(elapsedTime);
  }, [elapsedTime, player]);

  const onTouchStart = useCallback(
    event => {
      if (!sliderRef.current) return;
      event.preventDefault();
      onSeekStart(getSliderValueFromTouch(event.touches, sliderRef.current));
    },
    [onSeekStart]
  );

  const onTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (!sliderRef.current) return;
      event.preventDefault();
      onSeeking(getSliderValueFromTouch(event.touches, sliderRef.current));
    },
    [onSeeking]
  );

  const {
    bufferBackgroundColor,
    fillColor,
    progressBarBackgroundColor,
    progressBarElapsedColor
  } = themeColors;

  const elapsedProgressColor = disabled
    ? progressBarDisabledColor
    : progressBarElapsedColor;

  const events =
    device.os.name === "iOS"
      ? {
          onTouchEnd: onSeekEnd,
          onTouchMove: onTouchMove,
          onTouchStart: onTouchStart,
          readOnly: true
        }
      : {
          onInput: (event: ChangeEvent<HTMLInputElement>) =>
            onSeeking(parseEventValue(event)),
          onMouseDown: (event: React.MouseEvent) =>
            onSeekStart(parseEventValue(event)),
          onMouseUp: onSeekEnd
        };

  return (
    <>
      <Progress visibility="visible" margin="0 4px" isMobile={device.isMobile}>
        <Wrapper backgroundColor={progressBarBackgroundColor}>
          <BufferProgress
            aria-label={i18n("buffer_progress", { language })}
            backgroundColor={bufferBackgroundColor}
            max={duration}
            value={isLive ? 1 : buffered}
          />
        </Wrapper>
        <Wrapper backgroundColor={progressBarBackgroundColor}>
          <ElapsedProgress
            backgroundColor={elapsedProgressColor}
            max={duration}
            value={elapsedTime}
          />
        </Wrapper>
        <SeekSlider
          aria-label={i18n("seek_slider", { language })}
          background={elapsedProgressColor}
          disabled={disabled}
          max={duration}
          ref={sliderRef}
          value={elapsedTime}
          {...events}
        />
      </Progress>

      {!disabled && (
        <Time iconFillColor={fillColor} data-testid="time-left">
          {getFormattedTimeRemaining(elapsedTime, durationInSeconds)}
        </Time>
      )}
    </>
  );
};

export default AudioTimeLine;
